import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from './use-toast';

export interface PlanningGateItem {
  id: string;
  contrato_id: string;
  contrato_nome: string;
  contrato_numero: string;
  data_execucao: string;
  quantidade_executada: number;
  unidade: string;
  tipo: 'execucao' | 'retrabalho';
  item_codigo: string | null;
  item_descricao: string | null;
  observacoes: string | null;
  localizacao_descricao: string | null;
  qualidade_status: string | null;
  qualidade_em: string | null;
  qualidade_observacoes: string | null;
  status: string;
  registrado_por: string;
  created_at: string;
}

/** Fetch all executions in 'aguardando_planejamento' */
export function usePlanningGateQueue() {
  return useQuery({
    queryKey: ['planning-gate-queue'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_validacoes_execucao')
        .select(`
          id, contrato_id, data_execucao, quantidade_executada, unidade, tipo,
          planilha_item_id, observacoes, localizacao_descricao,
          qualidade_status, qualidade_em, qualidade_observacoes, status,
          registrado_por, created_at
        `)
        .eq('status', 'aguardando_planejamento')
        .order('data_execucao', { ascending: false });

      if (error) throw error;

      const items = data || [];
      const contratoIds = [...new Set(items.map((i: any) => i.contrato_id))];
      const planilhaItemIds = [...new Set(items.map((i: any) => i.planilha_item_id).filter(Boolean))];

      let contratoMap: Record<string, { nome: string; numero: string }> = {};
      if (contratoIds.length > 0) {
        const { data: contratos } = await supabase
          .from('emp_contratos')
          .select('id, nome, numero_contrato')
          .in('id', contratoIds);
        (contratos || []).forEach((c: any) => {
          contratoMap[c.id] = { nome: c.nome, numero: c.numero_contrato };
        });
      }

      let itemMap: Record<string, { codigo: string; descricao: string }> = {};
      if (planilhaItemIds.length > 0) {
        const { data: planItems } = await supabase
          .from('emp_planilha_itens')
          .select('id, codigo_item, descricao')
          .in('id', planilhaItemIds);
        (planItems || []).forEach((p: any) => {
          itemMap[p.id] = { codigo: p.codigo_item, descricao: p.descricao };
        });
      }

      return items.map((item: any) => ({
        ...item,
        contrato_nome: contratoMap[item.contrato_id]?.nome || '—',
        contrato_numero: contratoMap[item.contrato_id]?.numero || '—',
        item_codigo: item.planilha_item_id ? (itemMap[item.planilha_item_id]?.codigo || null) : null,
        item_descricao: item.planilha_item_id ? (itemMap[item.planilha_item_id]?.descricao || null) : null,
      })) as PlanningGateItem[];
    },
    refetchInterval: 30_000,
  });
}

/** Approve or reject planning gate */
export function usePlanningGateMutation() {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      execucaoId,
      contratoId,
      decision,
      observacoes,
    }: {
      execucaoId: string;
      contratoId: string;
      decision: 'aprovada' | 'rejeitada';
      observacoes: string;
    }) => {
      if (!user) throw new Error('Não autenticado');
      if (decision === 'rejeitada' && (!observacoes || observacoes.trim().length < 10)) {
        throw new Error('Rejeição requer justificativa com pelo menos 10 caracteres.');
      }

      const novoStatus = decision === 'aprovada' ? 'disponivel_para_medicao' : 'aguardando_qualidade';
      const { error } = await supabase.rpc('emp_transicionar_execucao', {
        p_execucao_id: execucaoId,
        p_novo_status: novoStatus,
        p_observacoes: observacoes?.trim() || null,
        p_motivo_rejeicao: decision === 'rejeitada' ? observacoes.trim() : null,
        p_certificado_evidencia_id: null,
      });

      if (error) throw error;
      return { execucaoId, contratoId, decision };
    },
    onSuccess: (vars) => {
      qc.invalidateQueries({ queryKey: ['planning-gate-queue'] });
      qc.invalidateQueries({ queryKey: ['emp-execucoes', vars.contratoId] });
      toast({
        title: vars.decision === 'aprovada'
          ? '✓ Planejamento aprovado — disponível para medição'
          : '✗ Retornado para qualidade',
      });
    },
    onError: (e: Error) => toast({
      title: 'Erro na transição',
      description: e.message,
      variant: 'destructive',
    }),
  });
}
